import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import { useQuery, queryOptions } from "@tanstack/react-query";
import { listBenzosPublic } from "@/lib/public-substances.functions";
import { NeonCard } from "@/components/NeonCard";
import { translateClass, translateVocab } from "@/i18n/strings";
import { useLang } from "@/i18n/LanguageContext";

type BenzoRow = {
  Substance: string;
  Class: string;
  "Equiv. to Diazepam 10mg": string;
  pharma?: Record<string, string>;
  warning?: boolean;
  nonComparable?: boolean;
};

const benzoQuery = queryOptions({
  queryKey: ["public-benzos"],
  queryFn: () => listBenzosPublic(),
});

const TH: React.CSSProperties = {
  background: "var(--accent-bg)",
  color: "var(--text-secondary)",
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  padding: "0.5rem 0.7rem",
  textAlign: "left",
  borderBottom: "1px solid var(--accent-border)",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const TD: React.CSSProperties = {
  padding: "0.45rem 0.7rem",
  fontSize: "0.85rem",
  color: "var(--text-primary)",
  borderBottom: "1px solid var(--accent-border)",
  verticalAlign: "top",
};

export type BenzoBlockVariant = "table" | "classes";

/**
 * Reusable benzodiazepine comparison block for harm reduction articles.
 * Values are rendered unchanged from the database, no conversion happens here.
 */
export function BenzoComparisonBlock({ variant = "table" }: { variant?: BenzoBlockVariant }) {
  const { lang } = useLang();
  const { data, isLoading, isError } = useQuery(benzoQuery);
  const rows = (data ?? []) as BenzoRow[];

  const groups = useMemo(() => {
    const map = new Map<string, BenzoRow[]>();
    for (const r of rows) {
      const key = r.pharma?.["Duration Category"] || r.Class || "";
      const list = map.get(key) ?? [];
      list.push(r);
      map.set(key, list);
    }
    return Array.from(map.entries());
  }, [rows]);

  if (isLoading) {
    return (
      <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", margin: "1.25rem 0" }}>
        Benzodiazepin-Vergleich wird geladen ...
      </p>
    );
  }

  if (isError || rows.length === 0) {
    return (
      <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", margin: "1.25rem 0" }}>
        Der Benzodiazepin-Vergleich ist gerade nicht verfügbar. Die vollständige Tabelle findest du unter{" "}
        <Link to="/benzo" style={{ color: "var(--accent)" }}>
          Benzodiazepine
        </Link>
        .
      </p>
    );
  }

  return (
    <div data-section="benzo" style={{ margin: "1.75rem 0" }}>
      <NeonCard style={{ padding: "1.1rem 1.25rem" }}>
        <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "var(--text-primary)" }}>
          {variant === "classes" ? "Benzodiazepine nach Wirkdauer" : "Benzodiazepine im Vergleich"}
        </h3>
        <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem", margin: "0.35rem 0 0.9rem" }}>
          {variant === "classes"
            ? "Gruppierung der veröffentlichten Wirkstoffe nach Wirkdauer, ohne Dosisangaben."
            : "Äquivalenzdosis bezogen auf Diazepam 10 mg. Werte unverändert aus der Datenbank, keine Dosisempfehlung."}
        </p>

        {variant === "classes" ? (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "0.75rem" }}>
            {groups.map(([key, list]) => (
              <li key={key || "ohne"}>
                <div
                  className="mono-badge"
                  style={{ display: "inline-block", marginBottom: "0.3rem" }}
                >
                  {key ? translateVocab(key, lang) : "Ohne Angabe"}
                </div>
                <div style={{ color: "var(--text-secondary)", fontSize: "0.88rem" }}>
                  {list.map((r) => r.Substance).join(", ")}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <>
            <p style={{ color: "var(--text-muted)", fontSize: "0.78rem", margin: "0 0 0.4rem" }}>
              Auf schmalen Bildschirmen lässt sich die Tabelle seitlich scrollen.
            </p>
            <div
              tabIndex={0}
              role="region"
              aria-label="Benzodiazepine im Vergleich, seitlich scrollbar"
              style={{ overflowX: "auto" }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
                <caption style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>
                  Benzodiazepine mit Klasse, Äquivalenzdosis zu Diazepam 10 mg, Halbwertszeit und Wirkdauer
                </caption>
                <thead>
                  <tr>
                    <th style={TH} scope="col">Wirkstoff</th>
                    <th style={TH} scope="col">Klasse</th>
                    <th style={TH} scope="col">Äquiv. zu Diazepam 10 mg</th>
                    <th style={TH} scope="col">Halbwertszeit</th>
                    <th style={TH} scope="col">Wirkdauer</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.Substance}>
                      <th scope="row" style={{ ...TD, fontWeight: 600, textAlign: "left" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}>
                          {r.Substance}
                          {r.warning && (
                            <span
                              style={{ display: "inline-flex", alignItems: "center", gap: "0.2rem", color: "var(--accent-strong)", fontSize: "0.72rem", fontWeight: 700 }}
                            >
                              <AlertTriangle size={13} aria-hidden="true" />
                              Warnung
                            </span>
                          )}
                        </span>
                      </th>
                      <td style={TD}>{translateClass(r.Class, lang)}</td>
                      <td style={TD}>{r.nonComparable ? "nicht vergleichbar" : r["Equiv. to Diazepam 10mg"] || "-"}</td>
                      <td style={TD}>{r.pharma?.["Half-Life"] || "-"}</td>
                      <td style={TD}>
                        {r.pharma?.["Duration Category"] ? translateVocab(r.pharma["Duration Category"], lang) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <p style={{ margin: "0.9rem 0 0" }}>
          <Link to="/benzo" style={{ color: "var(--accent)", fontWeight: 600, textDecoration: "none", fontSize: "0.88rem" }}>
            Vollständige Benzodiazepin-Tabelle ansehen
          </Link>
        </p>
      </NeonCard>
    </div>
  );
}
